// Single source of truth for the set of supported chart types.
//
// Lives in `shared/` because both the server (`authoring/validate-source.ts`,
// included in the Node tsc build) and the browser
// (`render/element/sbi/validate-render-op.ts`, bundled by Vite) need to read
// this list. `shared/` is the cross-cutting home for shapes that both halves
// of the split depend on; neither `render/` nor `authoring/` would be a
// neutral place to put a constant they both consume.
//
// The compile-time assertion below keeps this tuple in lock-step with the
// `ChartSpec` discriminated union in `render/element/sbi/sbi.d.ts` — drop a
// new spec into the union without listing it here and TypeScript fails the
// build. The `import type` is erased at emit, so the server bundle has no
// runtime dependency on the element subtree (the only edge from `shared/`
// back into `render/` is this type-only line and the matching one in
// `chart-spec-schema.ts`).

import type { ChartSpec } from "../render/element/sbi/sbi";

/**
 * Ordered tuple of every `type` discriminant the host knows how to render.
 * The order is irrelevant at runtime; we keep it stable so diffs read
 * cleanly when new types are appended.
 */
export const CHART_TYPES = [
  "line",
  "bar",
  "stacked_bar",
  "scatter",
  "histogram",
  "area",
  "heatmap",
  "boxplot",
  "radar",
  "waterfall",
  "gauge",
  "mixed",
  "polar_bar",
  "donut",
  "pie",
  "punchcard",
  "candlestick",
  "sunburst",
  "tree",
  "sankey",
  "funnel",
  "calendar",
] as const;

export type ChartType = (typeof CHART_TYPES)[number];

// Exhaustiveness assertion. If `ChartSpec["type"]` gains a member that
// isn't in `CHART_TYPES`, `Missing` resolves to a non-`never` union and
// the conditional below fails to compile. The reverse direction is
// covered by `ChartType extends ChartSpec["type"]`, which TS will reject
// if `CHART_TYPES` lists a string that no spec actually uses.
type Missing = Exclude<ChartSpec["type"], ChartType>;
type _AssertCoversAllChartSpecs = Missing extends never ? true : Missing;
type _AssertNoStrayChartTypes = ChartType extends ChartSpec["type"] ? true : never;
// Reference the assertions so unused-type lints don't strip them.
export type _ChartTypeAssertions = [
  _AssertCoversAllChartSpecs,
  _AssertNoStrayChartTypes,
];
